import { InjectionToken, Signal } from '@angular/core';

import type { AugmentedGoal } from 'src/app/domain/goals/types/Goal.model';
import type { Observation } from 'src/app/domain/observations/types/observations.types';
import type { Student, StudentNote } from 'src/app/domain/student/types/Student.model';

/**
 * Which roster a student surface is reading. `caseload` is the practitioner's
 * own assigned students (Notebook); `team` is every student on the active
 * partner (Admin).
 */
export type StudentScopeKind = 'caseload' | 'team';

/**
 * How a student is detached from the current surface. The two scopes mean
 * genuinely different things by "remove", so the shared page renders the
 * copy and confirmation for whichever its scope declares:
 *
 * - `remove-from-caseload` — Notebook. Drops the student from the signed-in
 *   practitioner's `studentIds`. The student record itself is untouched and
 *   other practitioners keep their access.
 * - `delete-student` — Admin. Deletes the student record for the whole
 *   partner via the callable in `AdminStudentsService`.
 * - `null` — the scope offers no removal affordance at all.
 */
export type StudentRemovalMode = 'remove-from-caseload' | 'delete-student' | null;

/**
 * The single seam the shared student-details page and the shared analytics
 * widgets read student, goal, and observation data through.
 *
 * Both Notebook and Admin render the same components; only the roster behind
 * them differs. Rather than branching inside every widget, each route provides
 * the implementation for its scope under {@link STUDENT_DATA_SCOPE} and the
 * components stay scope-agnostic.
 *
 * Implementations are thin adapters over the existing signal stores — they own
 * no state of their own, so a component reading through the port gets exactly
 * the same memoized signals it would have read directly.
 *
 * **Reads are signals, writes are methods.** Anything a template re-renders
 * from is exposed as a `Signal`; the `getXBy*` lookups are plain methods that
 * read the underlying signal, matching the store convention they wrap.
 *
 * @security FERPA - every member of this port carries student PII
 */
export interface StudentDataScope {
  /** Which roster this implementation reads. */
  readonly kind: StudentScopeKind;

  // ── Students ───────────────────────────────────────────────────────────

  /** Every student in scope. */
  readonly students: Signal<Student[]>;

  /** `studentId` → display name, for widgets that label rows by student. */
  readonly studentNameMap: Signal<Map<string, string>>;

  /** One student by id, or null when not in scope. */
  getStudentById(id: string): Student | null;

  // ── Student writes (optimistic; see the store implementations) ──────────

  /** Apply a partial update to a student record. */
  updateStudentFields(studentId: string, fields: Partial<Student>): void;

  /** Append a note and return it synchronously. */
  addNote(studentId: string, content: string): StudentNote;

  /** Rewrite one existing note's content. No-op when the note is unknown. */
  editNote(studentId: string, noteId: string, content: string): void;

  /** Drop one note. No-op when the note is unknown. */
  removeNote(studentId: string, noteId: string): void;

  /** Replace the accommodations list wholesale. */
  updateAccommodations(studentId: string, accommodations: string[]): void;

  /** Replace the modifications list wholesale. */
  updateModifications(studentId: string, modifications: string[]): void;

  /**
   * Perform this scope's removal action for a student, per
   * {@link StudentRemovalMode} — detach from the signed-in practitioner's
   * caseload, or delete the record for the whole partner. Resolves once the
   * backing write has succeeded and local state has been reconciled; rejects
   * with the underlying error otherwise, leaving local state untouched.
   */
  removeStudent(studentId: string): Promise<void>;

  /**
   * Drop a student from local state after its backing write succeeded. This
   * is the local-cache half only — the caller owns the Firestore/callable
   * side, which differs per scope (see {@link StudentRemovalMode}).
   */
  removeStudentLocally(studentId: string): void;

  // ── Goals ──────────────────────────────────────────────────────────────

  /**
   * Goals in progress monitoring — `status === 'active'` exactly.
   *
   * Normalising this is the point: `AdminGoalsState.activeGoals` means
   * `status !== 'completed'` (so it includes drafts) while
   * `MyGoalsState.activeGoals` means `status === 'active'`. Widgets reading
   * through the port always get the narrow meaning.
   */
  readonly activeGoals: Signal<AugmentedGoal[]>;

  /** Every goal for one student, any status. */
  getGoalsByStudentId(studentId: string): AugmentedGoal[];

  // ── Observations ───────────────────────────────────────────────────────

  /** Every observation in scope. */
  readonly observations: Signal<Observation[]>;

  /** Observations inside `RECENT_WINDOW_DAYS`. */
  readonly recentObservations: Signal<Observation[]>;

  /** Every observation for one student, newest-first ordering not guaranteed. */
  getObservationsByStudentId(studentId: string): Observation[];

  // ── Scope-specific navigation & capabilities ───────────────────────────

  /**
   * True when this scope may edit administrator-only student fields —
   * currently the disability categories, which drive eligibility and gate
   * goal creation. Practitioners can set them once while they are empty (the
   * goal-drafter needs at least one), but changing an existing set is an
   * administrator action, so only the Admin scope gets the edit affordance.
   */
  readonly canEditProtectedFields: boolean;

  /** Which removal affordance, if any, the details page should offer. */
  readonly removalMode: StudentRemovalMode;

  /**
   * True when this scope works goals in full: edit a draft, open a goal from
   * its card, generate a progress report. Notebook can; in Admin the goal
   * cards stay read-only because the goal, finalize, and progress-report pages
   * read the caseload only. Templates gate whole actions on this flag; the URL
   * builders below independently return null so a stray call can't navigate
   * somewhere that doesn't exist.
   */
  readonly canAuthorGoals: boolean;

  /**
   * True when this scope offers the add-a-goal and add-an-observation actions.
   * Both scopes do, while the district has Tools and is out of compatibility
   * mode. An administrator can do this for any student in the district.
   */
  readonly canAddGoalsAndObservations: boolean;

  /**
   * True when this scope may delete a student's goals, in any status, from
   * the Goals tab. Admin only: removing a goal of record is district
   * record-keeping. Practitioners can delete drafts alone, from the goal page,
   * so the Notebook scope leaves this off.
   */
  readonly canDeleteGoals: boolean;

  /** Goal detail page, or null when this scope has none. */
  goalUrl(goalId: string): string | null;

  /** Goal drafter for a student, or null when this scope can't author goals. */
  goalDrafterUrl(studentId: string): string | null;

  /** Data-collection page for a goal, or null when unavailable. */
  goalCollectUrl(goalId: string): string | null;

  /** Progress-report preview for a student, or null when unavailable. */
  progressReportUrl(studentId: string): string | null;

  /** URL of the list this scope's detail page returns to. */
  studentListUrl(): string;

  /** Canonical URL for one tab of one student's detail page. */
  studentTabUrl(studentId: string, tab: string): string;
}

/**
 * DI token for {@link StudentDataScope}. Provided per route — Notebook routes
 * bind the caseload implementation, Admin routes bind the team one.
 */
export const STUDENT_DATA_SCOPE = new InjectionToken<StudentDataScope>('STUDENT_DATA_SCOPE');
